import { Tooltip } from "antd";
import {
  ArpsDesignerKind,
  ProductNames,
  TypeWellInputTableMode
} from "constants/forecasts.constants";
import styled from "styled-components";

import { isRatio } from "../../../../../utils/arps/productHelpers";
import { formatProductName } from "../../../../../utils/arps/productMapping";
import { CumulativeData } from "../../../../forecasting/Forecasting";

interface ProductLabelParams {
  product: string;
  kind: string;
  productIndex: number;
  segmentIndex: number;
  cumulativeData: CumulativeData;
  mode: TypeWellInputTableMode;
  onChangePrimaryProduct?: (product: string) => void;
}

export const GetProductLabel = (props: ProductLabelParams) => {
  const {
    mode,
    cumulativeData,
    product,
    kind,
    productIndex,
    segmentIndex,
    onChangePrimaryProduct
  } = props;
  const isTypeWellFirstProductSegment =
    kind === ArpsDesignerKind.TypeWell && productIndex === 0 && segmentIndex === 0;

  const isPrimaryProductForForecasting = (product: string, primaryProduct?: string) => {
    return (
      product === primaryProduct ||
      (product === ProductNames.Condensate && primaryProduct === ProductNames.Oil)
    );
  };

  const isPrimaryProduct =
    isTypeWellFirstProductSegment ||
    (kind === ArpsDesignerKind.Forecasting &&
      isPrimaryProductForForecasting(product, cumulativeData?.primaryProduct));

  const isRatioProduct = isRatio(product);

  const shouldShowButton =
    !isRatioProduct &&
    (kind === ArpsDesignerKind.TypeWell ||
      (kind === ArpsDesignerKind.Forecasting && isPrimaryProduct));

  if (mode !== TypeWellInputTableMode.Product) {
    return <SegmentHeader>Segment {segmentIndex + 1}</SegmentHeader>;
  }

  const handleClick = () => {
    if (!isPrimaryProduct && onChangePrimaryProduct) {
      onChangePrimaryProduct(product);
    }
  };

  return (
    <ProductLabelContainer>
      {shouldShowButton && (
        <Tooltip title="Set as primary product">
          <PrimaryProductButton
            isPrimary={isPrimaryProduct}
            onClick={handleClick}
            disabled={isPrimaryProduct}>
            P
          </PrimaryProductButton>
        </Tooltip>
      )}
      <label data-testid="type-well-product-label">{formatProductName(product)}</label>
    </ProductLabelContainer>
  );
};

const SegmentHeader = styled.div`
  width: 100%;
  text-align: left;
`;

const ProductLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 1.2rem;
  line-height: 1;
`;

const PrimaryProductButton = styled.button<{ isPrimary: boolean }>`
  font-weight: bold;
  border-radius: var(--border-radius);
  cursor: ${(props) => (props.isPrimary ? "default" : "pointer")};
  padding: 3px 6px;
  width: 19px;
  height: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: all 0.2s var(--ease);
  font-size: 1.3rem;

  color: ${(props) => (props.isPrimary ? "white" : "var(--color-text-50)")};
  background: ${(props) => (props.isPrimary ? "var(--color-primary)" : "transparent")};
  border: none;

  :hover {
    color: ${(props) => (props.isPrimary ? "white" : "var(--color-primary)")};
    background: ${(props) => (props.isPrimary ? "var(--color-primary)" : "transparent")};
  }

  :disabled {
    cursor: default;
  }
`;
